
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  CARD_IMAGE_EDIT_OVERLAY_STORAGE_KEY,
  readCardImageEditOverlayPreference,
  writeCardImageEditOverlayPreference,
} from '@/utils/cardImageEditOverlayPreference';

interface CardImageEditOverlayPreferenceValue {
  showCardImageEditOverlay: boolean;
  setShowCardImageEditOverlay: (show: boolean) => void;
}

const CardImageEditOverlayPreferenceContext =
  createContext<CardImageEditOverlayPreferenceValue | undefined>(undefined);

export const CardImageEditOverlayPreferenceProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [showCardImageEditOverlay, setShowState] = useState(false);

  useEffect(() => {
    setShowState(readCardImageEditOverlayPreference());
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === CARD_IMAGE_EDIT_OVERLAY_STORAGE_KEY) {
        setShowState(readCardImageEditOverlayPreference());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setShowCardImageEditOverlay = useCallback((show: boolean) => {
    writeCardImageEditOverlayPreference(show);
    setShowState(show);
  }, []);

  const value = useMemo(
    () => ({ showCardImageEditOverlay, setShowCardImageEditOverlay }),
    [showCardImageEditOverlay, setShowCardImageEditOverlay]
  );

  return (
    <CardImageEditOverlayPreferenceContext.Provider value={value}>
      {children}
    </CardImageEditOverlayPreferenceContext.Provider>
  );
};

export const useCardImageEditOverlayPreference = (): CardImageEditOverlayPreferenceValue => {
  const ctx = useContext(CardImageEditOverlayPreferenceContext);
  if (!ctx) {
    throw new Error(
      'useCardImageEditOverlayPreference must be used within CardImageEditOverlayPreferenceProvider'
    );
  }
  return ctx;
};
